import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CButton } from '@coreui/react';
import { Helmet } from 'react-helmet';
import { imgUrl } from 'src/actions/types';

// --- Import necessary Redux actions ---
import { 
    getAllReviewsForAdmin,
    deleteReviewAndFeedback 
} from '../../actions/reviewActions';
import { getAllPatientProfiles } from '../../actions/patientProfileActions';
import { getAllDoctors } from '../../actions/doctorActions';

// --- Helper components and functions (Unchanged) ---
const RenderStars = ({ rating }) => {
    const totalStars = 5;
    const numRating = parseInt(rating, 10) || 0;
    const stars = [];
    for (let i = 0; i < totalStars; i++) {
        stars.push(<i key={i} className={`fe fe-star ${i < numRating ? 'text-warning' : 'text-secondary'}`} />);
    }
    return <>{stars}</>;
};

const formatTableDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    return (
        <>
            {date.toLocaleDateString('en-GB', dateOptions)}
            <br />
            <small className="text-muted">{date.toLocaleTimeString('en-US', timeOptions)}</small>
        </>
    );
};


// --- Main Admin Reviews Component ---
const Reviewsadmin = ({
    allReviews,
    patients,
    doctors,
    isLoading,
    getAllReviewsForAdmin,
    getAllPatientProfiles,
    getAllDoctors,
    deleteReviewAndFeedback
}) => {
    // --- State for the success/error feedback modal (Unchanged) ---
    const [feedbackModal, setFeedbackModal] = useState({
        isVisible: false,
        title: '',
        message: '',
        color: 'info'
    });
    const [isDeletingId, setIsDeletingId] = useState(null);

    // --- [NEW] State for the view review details modal ---
    const [viewingReview, setViewingReview] = useState(null);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);


    // --- Data Fetching (Unchanged) ---
    useEffect(() => {
        getAllReviewsForAdmin();
        getAllPatientProfiles();
        getAllDoctors();
    }, [getAllReviewsForAdmin, getAllPatientProfiles, getAllDoctors]);

    // --- Data Merging Logic (Unchanged) ---
    const enrichedReviews = useMemo(() => {
        if (!allReviews || !patients || !doctors || patients.length === 0 || doctors.length === 0) return [];
        const patientMap = new Map(patients.map(p => [String(p.PatientID), p]));
        const doctorMap = new Map(doctors.map(d => [String(d.hospitalID), d]));
        return allReviews
            .filter(item => item.type === 'review')
            .map(review => ({
                ...review,
                patient: patientMap.get(String(review.patientId)) || {},
                doctor: doctorMap.get(String(review.hospitalID)) || {},
            }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [allReviews, patients, doctors]);

    // --- [NEW] Handler to open the view modal ---
    const handleView = (review) => {
        setViewingReview(review);
        setIsViewModalVisible(true);
    };

    // --- Delete Handler (Unchanged) ---
    const handleDelete = (review) => {
        setIsDeletingId(review.id);
        deleteReviewAndFeedback(review.id)
            .then(() => {
                setFeedbackModal({
                    isVisible: true,
                    title: 'Success',
                    message: `The review from patient "${review.patient.firstName}" has been successfully deleted.`,
                    color: 'success'
                });
                getAllReviewsForAdmin();
            })
            .catch(err => {
                setFeedbackModal({
                    isVisible: true,
                    title: 'Error',
                    message: 'Could not delete the review. Please try again.',
                    color: 'danger'
                });
            })
            .finally(() => {
                setIsDeletingId(null);
            });
    };
    
    return (
        <Fragment>
            <Helmet>
                <title>All Reviews | Admin Dashboard</title>
                <meta name="description" content="Moderate and view all patient reviews across the platform." />
            </Helmet>
            
            <div className="page-wrapper ml-0 ">
                <div className="content-container">
                    <div className="page-header filter-bar rounded">
                              <div className="row align-items-center">
                                <div className="col">
                                  <h4 className="page-title"> Reviews</h4>
                                  <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                      <Link to="/doctor/dashboard">Admin Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Reviews</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="datatable table table-hover table-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Patient Name</th>
                                                    <th>Doctor Name</th>
                                                    <th>Ratings</th>
                                                    <th>Review Title</th>
                                                    <th>Date</th>
                                                    <th className="text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading && enrichedReviews.length === 0 ? (
                                                    <tr><td colSpan="6" className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div></td></tr>
                                                ) : enrichedReviews.length > 0 ? (
                                                    enrichedReviews.map((review) => {
                                                        const patientImg = review.patient.uploadPhoto ? `${imgUrl}${review.patient.uploadPhoto}` : "/assets/img/patients/patient.jpg";
                                                        const doctorImg = review.doctor.uploadPhoto ? `${imgUrl}${review.doctor.uploadPhoto}` : "/assets/img/doctors/doctor-thumb-01.jpg";
                                                        const isCurrentlyDeleting = isDeletingId === review.id;

                                                        return (
                                                            <tr key={review.id}>
                                                                <td><h2 className="table-avatar"><a  className="avatar avatar-sm mr-2"><img className="avatar-img rounded-circle" src={patientImg} alt="Patient" /></a><a >{review.patient.firstName} {review.patient.lastName}</a></h2></td>
                                                                <td><h2 className="table-avatar"><a  className="avatar avatar-sm mr-2"><img className="avatar-img rounded-circle" src={doctorImg} alt="Doctor" /></a><a>Dr. {review.doctor.firstName} {review.doctor.lastName}</a></h2></td>
                                                                <td><RenderStars rating={review.stars} /></td>
                                                                <td style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={review.title}>
                                                                    {review.title}
                                                                </td>
                                                                <td>{formatTableDate(review.createdAt)}</td>
                                                                <td className="text-right">
                                                                    <div className="actions">
                                                                        {/* --- [NEW] View Button Added --- */}
                                                                        <button 
                                                                            className="i-btn-view" 
                                                                            onClick={() => handleView(review)}
                                                                        >
                                                                        
                                                                        </button>
                                                                        
                                                                        <button 
                                                                            className="i-btn-delete"
                                                                            onClick={() => handleDelete(review)}
                                                                            disabled={isCurrentlyDeleting}
                                                                        >
                                                                            {isCurrentlyDeleting ? (
                                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                            ) : (
                                                                                <></>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr><td colSpan="6" className="text-center text-muted p-5">No reviews found on the platform.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>			
                    </div>
                </div>			
            </div>

            {/* --- [NEW] Review Details Modal --- */}
            {viewingReview && (
                 <CModal 
                    show={isViewModalVisible} 
                    onClose={() => setIsViewModalVisible(false)} 
                    centered
                >
                    <CModalHeader className="bg-info"closeButton closeVariant="white">
                        <CModalTitle>Review Details</CModalTitle>
                    </CModalHeader>
                    <CModalBody>
                        <ul className="list-group list-group-flush">
                            <li className="list-group-item">
                                <strong>Patient:</strong> {viewingReview.patient.firstName} {viewingReview.patient.lastName}
                            </li>
                            <li className="list-group-item">
                                <strong>Doctor:</strong> Dr. {viewingReview.doctor.firstName} {viewingReview.doctor.lastName}
                            </li>
                            <li className="list-group-item">
                                <strong>Rating:</strong> <RenderStars rating={viewingReview.stars} />
                            </li>
                            <li className="list-group-item">
                                <strong>Date:</strong> {new Date(viewingReview.createdAt).toLocaleString('en-GB')}
                            </li>
                            <li className="list-group-item">
                                <strong>Title:</strong>
                                <p className="mb-0">{viewingReview.title}</p>
                            </li>
                            <li className="list-group-item">
                                <strong>Description:</strong>
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                    {viewingReview.text || "No description provided."}
                                </p>
                            </li>
                        </ul>
                    </CModalBody>
                    <CModalFooter>
                        <button 
                        className="i-btn-cancel"
                            onClick={() => setIsViewModalVisible(false)}
                        >
                            
                        </button>
                    </CModalFooter>
                </CModal>
            )}

            {/* --- Feedback Modal (Unchanged) --- */}
            <CModal 
                visible={feedbackModal.isVisible} 
                onClose={() => setFeedbackModal({ ...feedbackModal, isVisible: false })} 
                centered
            >
                <CModalHeader closeButton className={`bg-${feedbackModal.color} text-white`}>
                    <CModalTitle>{feedbackModal.title}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p className="text-center my-3">{feedbackModal.message}</p>
                </CModalBody>
                <CModalFooter>
                    <CButton 
                        color={feedbackModal.color}
                        onClick={() => setFeedbackModal({ ...feedbackModal, isVisible: false })}
                    >
                        Close
                    </CButton>
                </CModalFooter>
            </CModal>

        </Fragment>
    );
};

// --- Redux Connection (Unchanged) ---
const mapStateToProps = (state) => ({
    allReviews: state.reviews.allReviews || [],
    patients: state.patientProfile.profiles || [],
    doctors: state.doctorList.doctors || [],
    isLoading: state.reviews.loading || state.patientProfile.loading || state.doctorList.loading,
});

const mapDispatchToProps = {
    getAllPatientProfiles,
    getAllDoctors,
    getAllReviewsForAdmin,
    deleteReviewAndFeedback,
};    

export default connect(mapStateToProps, mapDispatchToProps)(Reviewsadmin);